import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { motion } from "framer-motion";
import {
  ListChecks, DollarSign, Gauge, Clock, WifiOff, RefreshCw, Download, FileSpreadsheet, X, Bug, FileText,
  Monitor, Users as UsersIcon, Layers,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import StatCard from "@/components/dashboard/StatCard";
import { BarRow, GroupedBars } from "@/components/dashboard/Charts";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import { useRefreshAll } from "@/hooks/useRefreshAll";
import { getCompanyAnalytics, listCompanyRuns } from "@/api/company";
import { listExtRuns } from "@/api/extension";
import { listExeRuns } from "@/api/exe";
import { FLOWS, FLOW_BY_KEY, normalizeRun, flowStats, perDaySeries, withinDays, isBug } from "@/lib/flows";

const now = Date.now();
const iso = (h) => new Date(now - h * 3600000).toISOString();
const usd = (n) => `$${Number(n || 0).toFixed(2)}`;
// One CSV cell, quoted per RFC 4180.
//
// Values were interpolated raw, so a tester address or flow label containing a
// comma or quote silently shifted every following column. The leading apostrophe
// is the other half: a cell starting with = + - @ is executed as a FORMULA by
// Excel and Sheets, which turns an exported report into a way to run code on
// whoever opens it.
function csvCell(value) {
  if (value === null || value === undefined) return "";
  let text = String(value);
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const fmtDur = (s) => (!s ? "—" : s < 60 ? `${Number(s).toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`);

const RANGES = [{ d: 7, label: "7d" }, { d: 30, label: "30d" }, { d: 90, label: "90d" }];

const DEMO_ANALYTICS = {
  total_runs: 142, total_cost_usd: 2.84, pass_rate: 81,
  // Distinct cases, deliberately far below total_runs — re-runs are the norm.
  test_cases_run: 37,
  by_tester: [
    { tester: "anita@acme.com", runs: 84, cost_usd: 1.62 },
    { tester: "ben@acme.com", runs: 41, cost_usd: 0.83 },
    { tester: "carl@acme.com", runs: 17, cost_usd: 0.39 },
  ],
};
const mk = (n, flow, pass = 0.75) => Array.from({ length: n }, (_, i) => ({
  id: `${flow}-${i}`, status: Math.random() < pass ? "passed" : "failed",
  browser: ["chromium", "chromium", "edge"][i % 3], duration_seconds: 8 + (i % 5) * 6,
  created_at: iso((i * 7) % (90 * 24)), token_usage: { estimated_cost_usd: 0.02 + (i % 4) * 0.01 },
}));
const DEMO_DASH = mk(60, "dashboard", 0.82).map((r) => ({ ...r, tester: ["anita", "ben", "carl"][r.id.charCodeAt(10) % 3] + "@acme.com" }));
const DEMO_EXT = mk(30, "extension", 0.74);
const DEMO_EXE = mk(20, "exe", 0.7);

export default function CompanyAnalytics() {
  const [flow, setFlow] = useState("all");   // all | dashboard | extension | exe
  const [days, setDays] = useState(30);

  // Scoped to the SAME window as everything else on the page — the per-tester
  // figures used to be all-time while the flow figures were ranged.
  const an = useApiData(() => getCompanyAnalytics(days, flow), DEMO_ANALYTICS);
  const dash = useApiData(() => listCompanyRuns(500), DEMO_DASH);
  const ext = useApiData(() => listExtRuns(500), DEMO_EXT);
  const exe = useApiData(() => listExeRuns(500), DEMO_EXE);

  // useApiData only fetches on mount, so the range selector has to ask for the
  // new window itself. Skipped on the first render — mount already fetched it.
  const firstRange = useRef(true);
  const reloadAnalytics = an.reload;
  useEffect(() => {
    if (firstRange.current) { firstRange.current = false; return; }
    reloadAnalytics();
  }, [days, flow, reloadAnalytics]);

  const a = an.data || DEMO_ANALYTICS;
  const demo = an.demo || dash.demo || ext.demo || exe.demo;
  const loading = an.loading || dash.loading || ext.loading || exe.loading;

  const runsByFlow = useMemo(() => ({
    dashboard: (Array.isArray(dash.data) ? dash.data : []).map((r) => normalizeRun(r, "dashboard")),
    extension: (Array.isArray(ext.data) ? ext.data : []).map((r) => normalizeRun(r, "extension")),
    exe: (Array.isArray(exe.data) ? exe.data : []).map((r) => normalizeRun(r, "exe")),
  }), [dash.data, ext.data, exe.data]);

  // Per-flow, range-filtered comparison. Each flow's cost comes from its OWN
  // runs, filtered to the same window as its run count.
  //
  // The dashboard flow used to be assigned `a.total_cost_usd` instead — the
  // company's ALL-TIME spend across ALL THREE runners — so the paused cloud flow
  // was credited with every dollar the other two had ever spent, no matter which
  // range was selected, and the "all flows" Spend KPI then added the extension
  // and exe totals on top of that again.
  const comparison = useMemo(() => FLOWS.map((f) => {
    const ranged = withinDays(runsByFlow[f.key], days);
    const s = flowStats(ranged);
    return { ...f, s, cost: s.cost, ranged };
  }), [runsByFlow, days]);

  const scope = flow === "all"
    ? { runs: [...runsByFlow.dashboard, ...runsByFlow.extension, ...runsByFlow.exe] }
    : { runs: runsByFlow[flow] };
  const ranged = useMemo(() => withinDays(scope.runs, days), [scope.runs, days]);
  const st = useMemo(() => flowStats(ranged), [ranged]);
  const spend = flow === "all"
    ? comparison.reduce((s, c) => s + c.cost, 0)
    : comparison.find((c) => c.key === flow)?.cost || 0;

  // The FULL selected range, not min(days, 30). Capping the series at 30 days
  // while the count beneath it covered 90 meant the bars and the number under
  // them described different windows — the mismatch this chart was reported for.
  const sparkSeries = useMemo(() => perDaySeries(ranged, days), [ranged, days]);
  const spark = sparkSeries.data;
  const chartedRuns = useMemo(() => spark.reduce((n, v) => n + v, 0), [spark]);
  const maxRuns = Math.max(1, ...comparison.map((c) => c.s.total));

  // by browser (headed flows) within scope + range
  const byBrowser = useMemo(() => {
    const m = {};
    ranged.forEach((r) => { if (r.browser) m[r.browser] = (m[r.browser] || 0) + 1; });
    return Object.entries(m).map(([browser, n]) => ({ browser, n })).sort((x, y) => y.n - x.n);
  }, [ranged]);
  const maxBrowser = Math.max(1, ...byBrowser.map((b) => b.n));

  // Failures and bugs are NOT the same number: a run can fail without producing a
  // bug report. Both were absent from the KPI row — the failure count existed only
  // in the footer of the chart below, while "Runs" was shown twice.
  const bugCount = useMemo(() => ranged.filter(isBug).length, [ranged]);

  // Distinct test CASES exercised, from the server — a run row carries no case id,
  // and re-running one case ten times is ten runs but one case, so this cannot be
  // derived from the run lists.
  const casesRun = typeof a.test_cases_run === "number" ? a.test_cases_run : null;

  // Each run list is fetched with limit=500. Below that the client-side totals are
  // exact; at the cap they silently under-report, which is indistinguishable from a
  // quiet week. Detect it and say so rather than presenting a short count as fact.
  const RUN_FETCH_LIMIT = 500;
  const truncated = [dash, ext, exe].some(
    (src) => Array.isArray(src.data) && src.data.length >= RUN_FETCH_LIMIT);
  // The server counts every row in the window, so it is right even when the lists
  // are capped. It has no per-flow breakdown, hence "all flows" only.
  const totalRuns = flow === "all" && typeof a.total_runs === "number" ? a.total_runs : st.total;

  const testers = Array.isArray(a.by_tester) ? a.by_tester : [];
  const maxTesterCost = Math.max(1e-4, ...testers.map((t) => t.cost_usd || 0));
  // Every runner records which user started a run, and the aggregate is now
  // scoped to the selected flow — so this is meaningful on every tab. It used to
  // be limited to all/dashboard precisely because the numbers were unscoped.
  const showTesters = true;

  // Both exports describe the SAME numbers, so they are built once. Values stay
  // as numbers here — the CSV stringifies on the way out, while the workbook keeps
  // them numeric so Excel can actually sum and chart a column.
  function buildReport() {
    const flowLabel = flow === "all" ? "All flows" : (FLOW_BY_KEY[flow]?.label || flow);
    const meta = [
      ["Range", `last ${days} days`],
      ["Flow", flowLabel],
      ["Exported", new Date().toISOString()],
    ];
    const sections = [{
      name: "Flows",
      header: ["Flow", "Runs", "Pass %", "Cost (USD)", "Avg duration (s)"],
      rows: comparison.map((c) => [
        c.label, c.s.total, c.s.passRate,
        Number(c.cost.toFixed(4)), Number(c.s.avgDur.toFixed(1)),
      ]),
    }];
    if (byBrowser.length) {
      sections.push({ name: "Browsers", header: ["Browser", "Runs"],
        rows: byBrowser.map((b) => [b.browser, b.n]) });
    }
    // Only when the page is showing it. The tester block used to be written for
    // every flow, including the headed ones where these figures come from a
    // different source and mean something else.
    if (showTesters && testers.length) {
      sections.push({ name: "Testers", header: ["Tester", "Runs", "Cost (USD)"],
        rows: testers.map((t) => [t.tester, t.runs, Number(Number(t.cost_usd || 0).toFixed(4))]) });
    }
    return { meta, sections, flowLabel };
  }

  const exportName = (ext) => `company-analytics-${flow}-${days}d.${ext}`;

  function download(blob, filename) {
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url; el.download = filename;
    document.body.appendChild(el); el.click(); el.remove(); URL.revokeObjectURL(url);
  }

  function exportCsv() {
    const { meta, sections } = buildReport();
    // The window and scope belong INSIDE the file: the filename carries them, but
    // a sheet pasted into a report or renamed loses that, and the numbers then
    // look like all-time totals.
    const rows = [["AI-QA-Engine analytics"], ...meta];
    sections.forEach((sec) => rows.push([], [sec.name], sec.header, ...sec.rows));
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\r\n");
    // BOM so Excel reads it as UTF-8 — without it a tester named "José" arrives
    // mangled, which is exactly the kind of "wrong data" this export was reported for.
    download(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }), exportName("csv"));
  }

  // A real .xlsx, not a CSV with the extension renamed. One sheet per section is
  // the point of the format: a single flat sheet with blank-row separators is
  // just the CSV again, and Excel cannot filter or total across those breaks.
  function exportXlsx() {
    const { meta, sections } = buildReport();
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb, XLSX.utils.aoa_to_sheet([["AI-QA-Engine analytics"], [], ...meta]), "Summary");
    sections.forEach((sec) => {
      const ws = XLSX.utils.aoa_to_sheet([sec.header, ...sec.rows]);
      // Column widths from the content, so nothing lands as ####.
      ws["!cols"] = sec.header.map((h, i) => ({
        wch: Math.min(40, Math.max(String(h).length,
          ...sec.rows.map((r) => String(r[i] ?? "").length)) + 2),
      }));
      XLSX.utils.book_append_sheet(wb, ws, sec.name);
    });
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    download(new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }), exportName("xlsx"));
  }

  const { refreshing, refresh: refreshAll } = useRefreshAll(
    [an.reload, dash.reload, ext.reload, exe.reload],
    { label: "Analytics" },
  );

  const TABS = [{ key: "all", label: "All flows", icon: Layers }, ...FLOWS.map((f) => ({ key: f.key, label: f.label, icon: f.icon }))];

  return (
    <>
      <PageHeader
        title="Analytics"
        subtitle="Cost, throughput, and reliability across your three testing flows."
        actions={
          <>
            <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-0.5 dark:border-white/10 dark:bg-white/5">
              {RANGES.map((r) => (
                <button key={r.d} onClick={() => setDays(r.d)}
                  className={cn("rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
                    days === r.d ? "bg-brand-600 text-white shadow-soft" : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white")}>
                  {r.label}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={refreshAll} disabled={refreshing}>
              <RefreshCw size={16} className={cn(refreshing && "animate-spin")} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv}
              title="Comma-separated values — one flat file">
              <Download size={16} /> Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportXlsx}
              title="Excel workbook — a separate sheet per section, with real numbers">
              <FileSpreadsheet size={16} /> Export Excel
            </Button>
          </>
        }
      />

      {/* Flow toggle */}
      <div className="mb-5 flex flex-wrap gap-1.5">
        {TABS.map((t) => {
          const active = flow === t.key;
          const meta = FLOW_BY_KEY[t.key];
          return (
            <button key={t.key} onClick={() => setFlow(t.key)}
              className={cn("inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-medium transition-colors",
                active
                  ? (meta ? cn("border-transparent", meta.chip) : "border-transparent bg-brand-600 text-white")
                  : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5")}>
              <t.icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}

      {/* KPIs (scope + range) */}
      {truncated && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={14} className="mt-px shrink-0" />
          <span>
            Only the newest {RUN_FETCH_LIMIT.toLocaleString()} runs per flow were loaded, so the
            breakdowns below cover a sample. Total runs and Test cases run are counted
            on the server and stay exact — narrow the range for figures that all agree.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-7">
        <StatCard icon={ListChecks} label="Total runs" value={totalRuns.toLocaleString()} accent="brand" sub={`last ${days} days`} spark={spark} sparkTitles={sparkSeries.titles} />
        <StatCard icon={Gauge} label="Pass rate" value={`${st.passRate}%`} accent="emerald" sub={`${st.passed} passed`} delay={0.05} />
        {/* Same definitions as Runs & Bugs, via the shared isBug — the two pages
            describe one set of runs and must not disagree about it. */}
        <StatCard icon={X} label="Failures" value={st.failed.toLocaleString()} accent="rose"
          sub={st.total ? `${Math.round((st.failed / st.total) * 100)}% of runs` : "none yet"} delay={0.1} />
        <StatCard icon={Bug} label="Bugs" value={bugCount.toLocaleString()} accent="amber"
          sub="with a bug report" delay={0.15} />
        <StatCard icon={FileText} label="Test cases run" value={casesRun === null ? "—" : casesRun.toLocaleString()}
          accent="sky" sub="distinct cases" delay={0.2} />
        <StatCard icon={Clock} label="Avg duration" value={fmtDur(st.avgDur)} accent="sky" sub="per run" delay={0.25} />
        <StatCard icon={DollarSign} label="Spend" value={usd(spend)} accent="brand" sub={flow === "all" ? "all flows" : FLOW_BY_KEY[flow]?.label} delay={0.3} />
      </div>

      {/* Flow comparison + runs over time */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="card p-5 lg:col-span-2">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Flow comparison</h3>
          <p className="text-xs text-slate-400">runs · pass rate · cost · avg duration · last {days} days</p>
          <div className="mt-4 space-y-4">
            {comparison.map((c) => (
              <button key={c.key} onClick={() => setFlow(c.key)} className={cn("block w-full rounded-xl border p-3 text-left transition-colors",
                flow === c.key ? cn("border-transparent ring-1", c.ring) : "border-slate-100 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5")}>
                <div className="flex items-center justify-between">
                  <span className={cn("inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-xs font-semibold", c.chip)}><c.icon size={14} /> {c.label}</span>
                  <span className="text-xs text-slate-400">{c.s.passRate}% pass · {fmtDur(c.s.avgDur)} avg · {usd(c.cost)}</span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-white/10">
                    <div className={cn("h-full rounded-full", c.bar)} style={{ width: `${Math.round((c.s.total / maxRuns) * 100)}%` }} />
                  </div>
                  <span className="w-14 shrink-0 text-right text-sm font-semibold text-slate-800 dark:text-white tabular-nums">{c.s.total}</span>
                </div>
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.08 }} className="card flex flex-col p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-white">Runs over time</h3>
            <span className="text-xs text-slate-400">{flow === "all" ? "all flows" : FLOW_BY_KEY[flow]?.label}</span>
          </div>
          <div className="mt-6">
            {/* Bars, dated ticks and per-day tooltips instead of a bare line: a line
                through daily counts has no axis, no values and no dates, and with
                most days at zero it renders as a flat rule with one spike. */}
            <GroupedBars
              labels={sparkSeries.labels}
              titles={sparkSeries.titles}
              series={[{
                key: flow, label: flow === "all" ? "All flows" : (FLOW_BY_KEY[flow]?.label || flow),
                color: flow === "all" ? "bg-brand-500" : (FLOW_BY_KEY[flow]?.bar || "bg-brand-500"),
                data: spark,
              }]}
              height={140}
              empty={`No runs in the last ${days} days.`}
            />
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4 dark:border-white/10">
            {/* The SUM OF THE BARS, so the caption can never contradict the chart
                above it. The exact company-wide total lives on the KPI card. */}
            <div><p className="text-xs text-slate-400">Runs charted</p><p className="font-display text-lg font-bold text-slate-900 dark:text-white">{chartedRuns.toLocaleString()}</p></div>
            <div><p className="text-xs text-slate-400">Failures</p><p className="font-display text-lg font-bold text-rose-600 dark:text-rose-300">{st.failed.toLocaleString()}</p></div>
          </div>
        </motion.div>
      </div>

      {/* Breakdowns: by browser + by tester */}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="card p-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white"><Monitor size={15} /> By browser</h3>
          <div className="mt-4 space-y-3">
            {byBrowser.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">No browser data in this scope{flow === "dashboard" ? " (cloud runs don't report a browser)" : ""}.</p>
            ) : byBrowser.map((b) => (
              <BarRow key={b.browser} label={b.browser} value={b.n} max={maxBrowser} display={b.n.toLocaleString()} color="bg-sky-500" />
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.08 }} className="card p-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-white"><UsersIcon size={15} /> Spend by tester{" "}
            <span className="text-xs font-normal text-slate-400">
              ({flow === "all" ? "all flows" : FLOW_BY_KEY[flow]?.label || flow})
            </span>
          </h3>
          {testers.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No tester activity yet.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {testers.map((t) => (
                <BarRow key={t.tester} label={t.tester.split("@")[0]} sub={`${(t.runs || 0).toLocaleString()} runs`}
                  value={t.cost_usd || 0} max={maxTesterCost} display={usd(t.cost_usd)} />
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {loading && <p className="mt-4 text-center text-xs text-slate-400">Loading latest data…</p>}
    </>
  );
}
